import {
  AlertTypes,
  apiClient,
  setAlert,
  GridSortModel,
  GridFilterModel,
} from "@leap4/frontend";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { reportingBaseUrl } from "../../../config/generals";
import { useCampaignScope } from "../../scope/CampaignScopeProvider";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { extractErrorMessage, isCanceledError } from "../../../utils/functions";
import {
  createRequestCancellation,
  IRequestCancellation,
} from "../../../utils/requestCancellation";
import ReportTabs from "./base/reportTabs";
import {
  initialReportObj,
  ICalculatedMetric,
  IReport,
  IReportingResult,
  IReportColumnDef,
  IValidationErrors,
} from "./report.interface";
import { CursorCache, IPaginationModel, ReportContext } from "./reportContext";
import { useReportingMetadata } from "./hooks/useReportingMetadata";
import {
  formatNumericDisplay,
  formatTimestampUtc,
  getTimeFromAggregationPresets,
  isEventMetricKey,
  rawPixelOnlyRangeError,
  timestampDimensionKeys,
} from "./reports";
import { buildReportColumns } from "./grid/reportColumns";
import {
  buildQueryParams as buildQueryParamsFor,
  type ILiveQueryState,
} from "./query/buildQueryParams";

import { SavedTemplate, OpenTemplate } from "./templates/types";


const isSameSortModel = (a: GridSortModel, b: GridSortModel): boolean =>
  a.length === b.length &&
  a.every((item, i) => item.field === b[i].field && item.sort === b[i].sort);

interface CampaignReportingConfigResponse {
  events?: Array<{
    event: string;
    display_label?: string;
    is_active?: boolean;
    is_attributed?: boolean;
    is_predicted?: boolean;
  }>;
  metric_labels?: Array<{ metric_key: string; display_label: string }>;
  calculated_metrics?: ICalculatedMetric[];
}

const formatReportRow = (row: any, timestampKeys: Set<string>): any => {
  const formattedRow: any = { ...row };
  if (formattedRow.pixel_date) {
    formattedRow.pixel_date = dayjs(formattedRow.pixel_date).format(
      "YYYY-MM-DD"
    );
  }
  if (formattedRow.event_date) {
    formattedRow.event_date = dayjs(formattedRow.event_date).format(
      "YYYY-MM-DD"
    );
  }
  for (const key of Object.keys(formattedRow)) {
    if (timestampKeys.has(key) && formattedRow[key]) {
      formattedRow[key] = formatTimestampUtc(formattedRow[key]);
    }
  }
  return formattedRow;
};

const ReportingModule = () => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const selectedOrganizationId = useAppSelector(
    (state: any) => state.auth?.selectedOrganizationId
  );

  const metadata = useReportingMetadata();
  const timestampKeys = useMemo(
    () => timestampDimensionKeys(metadata.dimensions),
    [metadata.dimensions]
  );
  const [report, setReport] = useState<IReport>(initialReportObj);
  const [showReportSettings, setShowReportSettings] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [reportTemplates, setReportTemplates] = useState<SavedTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);
  const [templateBusy, setTemplateBusy] = useState(false);
  const scopeRef = useRef(selectedOrganizationId);
  scopeRef.current = selectedOrganizationId;
  const templateListRequest = useRef(0);
  const refreshTemplates = useCallback(async () => {
    const scope = selectedOrganizationId;
    const request = ++templateListRequest.current;
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const response = await apiClient.get<SavedTemplate[]>(
        `${reportingBaseUrl}/templates`
      );
      if (scopeRef.current === scope && request === templateListRequest.current)
        setReportTemplates(response.data);
    } catch (error) {
      if (scopeRef.current === scope && request === templateListRequest.current)
        setTemplatesError(
          extractErrorMessage(error) || "Could not load saved templates."
        );
    } finally {
      if (scopeRef.current === scope && request === templateListRequest.current)
        setTemplatesLoading(false);
    }
  }, [selectedOrganizationId]);
  useEffect(() => {
    setReportTemplates([]);
    resetGridSortAndFilter();
    cursorCacheRef.current = new Map([[0, null]]);
    paginationRef.current = { page: 0, pageSize: 100, cursor: null };
    setPaginationModel(paginationRef.current);
    reportCancellationRef.current?.cancel("Organization changed");
    runDatesRef.current = null;
    chartRequestId.current += 1;
    setChartRows(null);
    setChartLoading(false);
    setColumns([]);
    updateReportTotals(null);
    setTotalCount(0);
    setReportError(null);
    void queryClient.resetQueries("runConversionReport", { exact: true });
    setReport((prev) => ({ ...initialReportObj, idCampaign: prev.idCampaign }));
    void refreshTemplates();
    return () => {
      templateListRequest.current += 1;
    };
  }, [refreshTemplates, queryClient]);
  const [columns, setColumns] = useState<IReportColumnDef[]>([]);
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>(
    {}
  );
  const [totalCount, setTotalCount] = useState(0);
  const [reportError, setReportError] = useState<string | null>(null);

  const [reportTotals, setReportTotals] = useState<IReportingResult | null>(
    null
  );

  const reportTotalsRef = useRef<IReportingResult | null>(null);
  const updateReportTotals = useCallback((value: IReportingResult | null) => {
    reportTotalsRef.current = value;
    setReportTotals(value);
  }, []);
  const [paginationModel, setPaginationModel] = useState<IPaginationModel>({
    page: 0,
    pageSize: 100,
    cursor: null,
    nextCursor: null,
    hasMore: false,
  });
  const paginationRef = useRef<IPaginationModel>({
    page: 0,
    pageSize: 100,
    cursor: null,
    nextCursor: null,
    hasMore: false,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const sortModelRef = useRef<GridSortModel>([]);
  const gridFilterModelRef = useRef<GridFilterModel>({ items: [] });
  const gridChangeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runDatesRef = useRef<{
    startDate: string | null;
    endDate: string | null;
  } | null>(null);
  const reportCancellationRef = useRef<IRequestCancellation | null>(null);

  const reportCancelledByUserRef = useRef(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportingLeadIds, setIsExportingLeadIds] = useState(false);
  const cursorCacheRef = useRef<CursorCache>(new Map([[0, null]]));
  const { campaigns: accessibleCampaigns, campaign: scopeCampaign, setCampaign: setScopeCampaign } = useCampaignScope();
  const [campaignEventOptions, setCampaignEventOptions] = useState<
    Array<{
      key: string;
      value: string;
      isAttributed: boolean;
      isPredicted: boolean;
    }>
  >([]);
  const [campaignConfigKey, setCampaignConfigKey] = useState("");
  const expectedCampaignConfigKey = `${String(selectedOrganizationId)}:${
    report.idCampaign
  }`;
  const [campaignCalculatedMetrics, setCampaignCalculatedMetrics] = useState<
    ICalculatedMetric[]
  >([]);
  const campaignCalculatedMetricsRef = useRef<ICalculatedMetric[]>([]);

  useEffect(() => {
    campaignCalculatedMetricsRef.current = campaignCalculatedMetrics;
  }, [campaignCalculatedMetrics]);

  useEffect(
    () => () =>
      reportCancellationRef.current?.cancel("Left the reporting page"),
    []
  );

  // The header's campaign is the report's campaign; the shell owns the list and the org scoping.
  useEffect(() => {
    if (!scopeCampaign) return;
    setReport((prev) => (prev.idCampaign === scopeCampaign ? prev : { ...prev, idCampaign: scopeCampaign }));
  }, [scopeCampaign]);

  useEffect(() => {
    let cancelled = false;
    setCampaignConfigKey("");
    if (!report.idCampaign) {
      setCampaignEventOptions([]);
      setCampaignCalculatedMetrics([]);
      setReport((prev) => ({ ...prev, customLabels: [] }));
      return () => {
        cancelled = true;
      };
    }

    const fetchCampaignReportingConfig = async () => {
      try {
        // Config is per campaign; a multi-campaign report offers the union of their events.
        const selected =
          report.idCampaign === "all"
            ? accessibleCampaigns
            : report.idCampaign.split(",").filter(Boolean);
        const responses = await Promise.all(
          selected.map((campaign) =>
            apiClient.get<CampaignReportingConfigResponse>(
              `${reportingBaseUrl}/campaign-config?id_campaign=${encodeURIComponent(campaign)}`
            )
          )
        );
        if (cancelled) return;
        const single = selected.length === 1;
        const config = single
          ? responses[0]?.data || {}
          : {
              events: Array.from(
                new Map(
                  responses
                    .flatMap((r) => r?.data?.events || [])
                    .map((e) => [String(e.event || "").trim().toLowerCase(), { ...e, is_predicted: false }])
                ).values()
              ),
              metric_labels: [],
              calculated_metrics: [],
            };
        const eventOptions = (config.events || [])
          .filter((event) => event?.is_active !== false)
          .map((event) => ({
            key: String(event.event || "")
              .trim()
              .toLowerCase(),
            value: event.display_label || event.event || "",

            isAttributed: event.is_attributed !== false,
            isPredicted: event.is_predicted === true,
          }))
          .filter((event) => event.key.length > 0);
        const customLabels = (config.metric_labels || [])
          .map((metric) => ({
            key: String(metric.metric_key || "").trim(),
            value: String(metric.display_label || "").trim(),
          }))
          .filter((metric) => metric.key.length > 0 && metric.value.length > 0);
        const calculatedMetrics = config.calculated_metrics ?? [];
        const newCalcKeys = new Set(
          calculatedMetrics
            .filter((c) => c.target_mode === "new")
            .map((c) => c.key)
        );
        // Catalogue on top of the hardcoded floor: a core metric missing from both is pruned here.
        const coreMetricKeys = new Set([
          "served_impression_count",
          "matched_impression_count",
          "advertiser_event_count",
          ...metadata.metrics.map((m) => m.key),
        ]);
        setCampaignConfigKey(
          `${String(selectedOrganizationId)}:${report.idCampaign}`
        );
        setCampaignEventOptions(eventOptions);
        setCampaignCalculatedMetrics(calculatedMetrics);
        setReport((prev) => ({
          ...prev,
          customLabels,
          metrics: prev.metrics.filter(
            (m) =>
              isEventMetricKey(m.eventType) ||
              m.eventType.startsWith("predicted::") ||
              coreMetricKeys.has(m.eventType) ||
              newCalcKeys.has(m.eventType)
          ),
        }));
      } catch (err) {
        if (cancelled) return;
        setCampaignEventOptions([]);
        setCampaignCalculatedMetrics([]);
      }
    };

    fetchCampaignReportingConfig();
    return () => {
      cancelled = true;
    };
    // accessibleCampaigns matters only for "all"; it changes with the org, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report.idCampaign, selectedOrganizationId]);

  const validateFields = (
    reportToValidate: IReport,
    isForSavingTemplates: boolean
  ): boolean => {
    const errors: IValidationErrors = {};
    if (!metadata.loaded || campaignConfigKey !== expectedCampaignConfigKey)
      errors.metrics = "Campaign settings are still loading. Please wait.";
    if (!reportToValidate.idCampaign) errors.idCampaign = "Choose a campaign.";

    if (!reportToValidate.name && isForSavingTemplates)
      errors.name = "Template Name is required";
    if (
      !reportToValidate.startDate ||
      Number.isNaN(new Date(reportToValidate.startDate).getTime())
    )
      errors.startDate = "Start Date is required";
    if (
      !reportToValidate.endDate ||
      Number.isNaN(new Date(reportToValidate.endDate).getTime())
    )
      errors.endDate = "End Date is required";
    if (reportToValidate.dimensions.length === 0)
      errors.dimensions = "At least one dimension is required";
    if (reportToValidate.metrics.length === 0)
      errors.metrics = "At least one metric is required";

    // The builder disables Run on this, but a saved template or a restored report can still arrive
    // out of range, so the same check gates the submit.
    if (!errors.startDate && !errors.endDate) {
      const rangeError = rawPixelOnlyRangeError(
        reportToValidate.metrics,
        reportToValidate.startDate,
        reportToValidate.endDate
      );
      if (rangeError) errors.startDate = rangeError;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const liveQueryState = (): ILiveQueryState => ({
    sortModel: sortModelRef.current,
    gridFilterModel: gridFilterModelRef.current,
    reportTotals: reportTotalsRef.current,
    campaignCalculatedMetrics: campaignCalculatedMetricsRef.current,
  });

  const buildQueryParams = (
    reportData: IReport,
    pagination: IPaginationModel,
    useCursor: boolean = true,
    applySort: boolean = true
  ): URLSearchParams =>
    buildQueryParamsFor(
      reportData,
      pagination,
      liveQueryState(),
      useCursor,
      applySort
    );

  const runReport = async (): Promise<IReportingResult[]> => {
    const resolved = formatReportDates(report);
    const queryReport = {
      ...report,
      ...(runDatesRef.current || {
        startDate: resolved.startDate,
        endDate: resolved.endDate,
      }),
    };
    if (!validateFields(queryReport, false)) {
      return [];
    }

    setReportError(null);
    chartServedInlineRef.current = false;

    reportCancellationRef.current?.cancel();
    const cancellation = createRequestCancellation();
    reportCancellationRef.current = cancellation;

    try {
      const params = buildQueryParams(queryReport, paginationRef.current);

      const response = await apiClient.get<{
        data: IReportingResult[];
        totalCount: number;
        nextCursor?: string | null;
        hasMore?: boolean;
        totals?: IReportingResult | null;
        series?: IReportingResult[];
      }>(`${reportingBaseUrl}?${params.toString()}`, cancellation.config);

      // The statement rolled the whole report up by date as well, so the chart's own scan of the
      // same rows is not issued at all. Absent, the caller falls back to fetching it.
      const inlineSeries = response?.data?.series;
      chartServedInlineRef.current = !!inlineSeries;
      if (inlineSeries) {
        chartRequestId.current += 1;
        setChartRows(
          inlineSeries.map((row) => formatReportRow(row, timestampKeys))
        );
      }

      const newTotalCount = response?.data?.totalCount;
      if (typeof newTotalCount === "number" && newTotalCount >= 0) {
        setTotalCount(newTotalCount);
      }

      if (
        response?.data &&
        response.data.totals !== undefined &&
        reportTotalsRef.current === null
      ) {
        updateReportTotals(response.data.totals ?? null);
      }

      const nextCursor = response?.data?.nextCursor || null;
      const hasMore = response?.data?.hasMore ?? false;

      if (nextCursor) {
        cursorCacheRef.current.set(paginationRef.current.page + 1, nextCursor);
      }

      paginationRef.current = { ...paginationRef.current, nextCursor, hasMore };
      setPaginationModel((prev) => ({ ...prev, nextCursor, hasMore }));

      if (!response?.data?.data || response.data.data.length === 0) {
        return [];
      }

      const formattedData = response.data.data.map((row) =>
        formatReportRow(row, timestampKeys)
      );

      setColumns(getReportColumns(formattedData[0]));
      return formattedData;
    } catch (err) {
      if (isCanceledError(err)) {
        if (reportCancelledByUserRef.current) {
          reportCancelledByUserRef.current = false;
          dispatch(
            setAlert({
              msg: "Report cancelled",
              type: AlertTypes.WARNING,
              d: dispatch,
            })
          );
        }
        throw err;
      }
      console.error("Failed to fetch reporting:", err);

      const message =
        extractErrorMessage(err) ?? "Could not fetch reporting data";
      setReportError(message);
      dispatch(
        setAlert({ msg: message, type: AlertTypes.FAILURE, d: dispatch })
      );
      return [];
    } finally {
      if (reportCancellationRef.current === cancellation) {
        reportCancellationRef.current = null;
      }
    }
  };

  const cancelReport = useCallback(() => {
    // One Run Report issues two reads — the grid's and the chart's. Cancelling only the grid
    // leaves the chart's scan running on the server for the rest of its natural life.
    chartCancellationRef.current?.cancel("Report cancelled by user");
    chartCancellationRef.current = null;
    // The grid's promise resolves even when its request was killed, so without this the cancelled
    // Run still goes on to start the chart's scan.
    chartDispatchPendingRef.current = false;
    if (!reportCancellationRef.current) return;
    reportCancelledByUserRef.current = true;
    reportCancellationRef.current.cancel("Report cancelled by user");
    reportCancellationRef.current = null;
  }, []);

  /**
   * The chart's own read: the same report grouped by date alone, unpaginated. The grid is one page of
   * a cohort-split result, and with several dimensions a page is often a single day — charting it
   * drew a line one point long. Cheap despite being a second request: with no cohort dimensions it
   * answers from the coarse daily rollup.
   *
   * Constraints are dropped deliberately — they are comparisons against the grid's grouping, and a
   * per-date row is not that row. Predictions are dropped because they arrive from their own endpoint.
   */
  const chartRequestId = useRef(0);
  const chartServedInlineRef = useRef(false);
  /** One chart read per Run. A second dispatch would cancel the first and leave the chart blank. */
  const chartDispatchPendingRef = useRef(false);
  const chartCancellationRef = useRef<IRequestCancellation | null>(null);
  const [chartRows, setChartRows] = useState<IReportingResult[] | null>(null);
  const [chartLoading, setChartLoading] = useState(false);
  const runChartQuery = useCallback(async () => {
    const requestId = ++chartRequestId.current;
    // Discarding the response is not enough: without a cancel token the statement runs to
    // completion on the server. Harmless when this answered from the rollup, not when a
    // raw-scan metric makes it a second full scan of adte_pixel.
    chartCancellationRef.current?.cancel();
    const chartCancellation = createRequestCancellation();
    chartCancellationRef.current = chartCancellation;
    setChartRows(null);
    setChartLoading(false);
    const resolved = formatReportDates(report);
    const queryReport = {
      ...report,
      ...(runDatesRef.current || {
        startDate: resolved.startDate,
        endDate: resolved.endDate,
      }),
    };
    const params = buildQueryParams(
      queryReport,
      { page: 0, pageSize: 0, cursor: null },
      false,
      false
    );
    // A Sum report still charts by day; the axis is the date the report is about.
    const dateKey =
      (params.get("dimensions") || "")
        .split(",")
        .find((d) => d === "pixel_date" || d === "event_date") ??
      (queryReport.reportType === "advertiser" ? "event_date" : "pixel_date");

    params.delete("page");
    params.delete("pageSize");
    params.delete("cursor");
    params.delete("predictedEvents");
    // Constraints act on the grid's rows, not on whole days; a day's total would pass them anyway.
    Array.from(params.keys())
      .filter((k) => k.startsWith("constraint_"))
      .forEach((k) => params.delete(k));
    params.set("dimensions", dateKey);
    if (queryReport.timeAggregation !== "Month") params.set("timeAggregation", "Day");
    params.set("includeTotals", "false");
    setChartLoading(true);

    try {
      const response = await apiClient.get<{ data: IReportingResult[] }>(
        `${reportingBaseUrl}?${params.toString()}`,
        chartCancellation.config
      );
      if (requestId !== chartRequestId.current) return;
      setChartRows(
        (response?.data?.data ?? []).map((row) =>
          formatReportRow(row, timestampKeys)
        )
      );
      setChartLoading(false);
    } catch {
      if (requestId !== chartRequestId.current) return;
      // The grid is unaffected, and the chart simply keeps to the predictions.
      setChartRows(null);
    setChartLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const {
    refetch: updateReportRef,
    data: reportData,
    isFetching: isFetchingReportData,
  } = useQuery("runConversionReport", runReport, {
    enabled: false,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    keepPreviousData: false,

    retry: false,
    onError: (err: any) => {
      console.error("Error running report:", err);
    },
    onSuccess: () => {
      console.log("Report executed successfully");
    },
  });

  const getReportColumns = (data: any): IReportColumnDef[] =>
    buildReportColumns(data, { report, metadata, campaignCalculatedMetrics });

  const formatReportDates = (reportToFormat: IReport): IReport => {
    let formattedReport = { ...reportToFormat };

    if (formattedReport.preset !== "choose time preset") {
      const { startDate, endDate } = getTimeFromAggregationPresets(
        formattedReport.preset,
        formattedReport.timezone
      );
      if (startDate && endDate) {
        formattedReport = { ...formattedReport, startDate, endDate };
      }
    }

    return {
      ...formattedReport,
      startDate: formattedReport.startDate
        ? dayjs(formattedReport.startDate).format("YYYY-MM-DD")
        : null,
      endDate: formattedReport.endDate
        ? dayjs(formattedReport.endDate).format("YYYY-MM-DD")
        : null,
    };
  };

  const saveReportTemplate = async (
    name: string,
    description: string,
    asCopy: boolean
  ): Promise<boolean> => {
    const scope = selectedOrganizationId;
    const formatted = formatReportDates({
      ...report,
      name: name.trim(),
      description: description.trim(),
    });
    if (!validateFields(formatted, true)) {
      throw new Error("Complete the report configuration before saving.");
    }
    setTemplateBusy(true);
    try {
      const editing = !!report._id && !asCopy;
      const payload = {
        name: formatted.name,
        description: formatted.description,
        config: formatted,
        revision: report.templateRevision,
      };
      const response = editing
        ? await apiClient.put<{ id: string; revision: number }>(
            `${reportingBaseUrl}/templates/${report._id}`,
            payload
          )
        : await apiClient.post<{ id: string; revision: number }>(
            `${reportingBaseUrl}/templates`,
            payload
          );
      if (scopeRef.current !== scope) return false;
      setReport((prev) => ({
        ...prev,
        _id: response.data.id,
        name: formatted.name,
        description: formatted.description,
        templateRevision: response.data.revision,
      }));
      await refreshTemplates();
      if (scopeRef.current !== scope) return false;
      dispatch(
        setAlert({
          msg: editing
            ? "Template updated."
            : "Template saved for your organization.",
          type: AlertTypes.SUCCESS,
          d: dispatch,
        })
      );
      return true;
    } finally {
      setTemplateBusy(false);
    }
  };

  const resetGridSortAndFilter = () => {
    if (gridChangeTimerRef.current) {
      clearTimeout(gridChangeTimerRef.current);
      gridChangeTimerRef.current = null;
    }
    sortModelRef.current = [];
    gridFilterModelRef.current = { items: [] };
    setSortModel([]);
    setGridFilterModel({ items: [] });
  };

  const runReportTemplate = async (template: SavedTemplate) => {
    const scope = selectedOrganizationId;
    setTemplateBusy(true);
    try {
      const response = await apiClient.get<OpenTemplate>(
        `${reportingBaseUrl}/templates/${template.id}`
      );
      if (scopeRef.current !== scope) return;
      const saved = response.data;
      // A template carries its campaign; loading it moves the whole portal there, or refuses if access is gone.
      const savedCampaign = saved.config?.idCampaign;
      let campaignNotice: string | null = null;
      let targetCampaign = scopeCampaign;
      if (savedCampaign && savedCampaign !== "all" && savedCampaign !== scopeCampaign) {
        if (!accessibleCampaigns.includes(savedCampaign)) {
          dispatch(
            setAlert({
              msg: `This template belongs to ${savedCampaign}, which your account can no longer access.`,
              type: AlertTypes.FAILURE,
              d: dispatch,
            })
          );
          return;
        }
        targetCampaign = savedCampaign;
        campaignNotice = `Campaign changed to ${savedCampaign} to match this template.`;
      } else if (savedCampaign === "all") {
        targetCampaign = "all";
      }
      reportCancellationRef.current?.cancel("Opened a template");
      runDatesRef.current = null;
      chartRequestId.current += 1;
      setReport(
        formatReportDates({
          ...initialReportObj,
          ...saved.config,
          idCampaign: targetCampaign,
          _id: saved.id,
          name: saved.name,
          description: saved.description,
          templateRevision: saved.revision,
        })
      );
      if (targetCampaign !== "all" && targetCampaign !== scopeCampaign) setScopeCampaign(targetCampaign);
      setValidationErrors({});
      setTabIndex(0);
      resetGridSortAndFilter();
      cursorCacheRef.current = new Map([[0, null]]);
      paginationRef.current = { page: 0, pageSize: 100, cursor: null };
      setPaginationModel(paginationRef.current);
      void queryClient.resetQueries("runConversionReport", { exact: true });
      setColumns([]);
      setChartRows(null);
    setChartLoading(false);
      updateReportTotals(null);
      setTotalCount(0);
      setReportError(null);
      dispatch(
        setAlert({
          msg: `Template loaded. ${campaignNotice ? `${campaignNotice} ` : ""}Review the configuration, then run the report.`,
          type: AlertTypes.SUCCESS,
          d: dispatch,
        })
      );
    } catch (error) {
      if (scopeRef.current === scope)
        dispatch(
          setAlert({
            msg:
              extractErrorMessage(error) ||
              "This template could not be opened. Its saved fields may no longer be available.",
            type: AlertTypes.FAILURE,
            d: dispatch,
          })
        );
    } finally {
      setTemplateBusy(false);
    }
  };

  const deleteReport = async (template: SavedTemplate) => {
    const scope = selectedOrganizationId;
    setTemplateBusy(true);
    try {
      await apiClient.delete(
        `${reportingBaseUrl}/templates/${template.id}?revision=${template.revision}`
      );
      if (scopeRef.current !== scope) return;
      await refreshTemplates();
      if (scopeRef.current !== scope) return;
      setReport((prev) =>
        prev._id === template.id
          ? { ...prev, _id: null, templateRevision: undefined }
          : prev
      );
    } finally {
      setTemplateBusy(false);
    }
  };
  const updateReport = runReportTemplate;

  /** Replaces a saved template's settings with the builder's current ones; the template keeps its name. */
  const overwriteTemplate = async (template: SavedTemplate) => {
    const scope = selectedOrganizationId;
    const formatted = formatReportDates({ ...report, name: template.name, description: template.description });
    if (!validateFields(formatted, true)) {
      throw new Error("Complete the report configuration in the builder before overwriting a template.");
    }
    setTemplateBusy(true);
    try {
      const response = await apiClient.put<{ id: string; revision: number }>(`${reportingBaseUrl}/templates/${template.id}`, {
        name: template.name,
        description: template.description,
        config: formatted,
        revision: template.revision,
      });
      if (scopeRef.current !== scope) return;
      if (report._id === template.id) setReport((prev) => ({ ...prev, templateRevision: response.data.revision }));
      await refreshTemplates();
      if (scopeRef.current !== scope) return;
      dispatch(setAlert({ msg: `"${template.name}" now holds the builder's current settings.`, type: AlertTypes.SUCCESS, d: dispatch }));
    } finally {
      setTemplateBusy(false);
    }
  };

  /** "Name (copy)", then "(copy 2)" and on, so a copy never collides with an existing name. */
  const copyTemplate = async (template: SavedTemplate) => {
    const scope = selectedOrganizationId;
    const taken = new Set(reportTemplates.map((t) => t.name.trim().toLowerCase()));
    let name = `${template.name} (copy)`;
    for (let n = 2; taken.has(name.toLowerCase()); n += 1) name = `${template.name} (copy ${n})`;
    setTemplateBusy(true);
    try {
      const opened = await apiClient.get<OpenTemplate>(`${reportingBaseUrl}/templates/${template.id}`);
      if (scopeRef.current !== scope) return;
      await apiClient.post(`${reportingBaseUrl}/templates`, {
        name,
        description: opened.data.description,
        config: { ...opened.data.config, name },
      });
      if (scopeRef.current !== scope) return;
      await refreshTemplates();
      if (scopeRef.current !== scope) return;
      dispatch(setAlert({ msg: `Saved a copy as "${name}".`, type: AlertTypes.SUCCESS, d: dispatch }));
    } finally {
      setTemplateBusy(false);
    }
  };

  const clearReport = () => {
    reportCancellationRef.current?.cancel("Cleared report");
    runDatesRef.current = null;
    chartRequestId.current += 1;
    void queryClient.resetQueries("runConversionReport", { exact: true });
    setChartRows(null);
    setChartLoading(false);
    setColumns([]);
    setReportError(null);
    setReport({
      ...initialReportObj,
      idCampaign: scopeCampaign || accessibleCampaigns[0] || "",
    });
    setShowReportSettings(false);
    setValidationErrors({});
    resetGridSortAndFilter();
    cursorCacheRef.current = new Map([[0, null]]);
    setPaginationModel({
      page: 0,
      pageSize: 100,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    });
    paginationRef.current = {
      page: 0,
      pageSize: 100,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setTotalCount(0);
    updateReportTotals(null);
  };

  const exportLeadIds = useCallback(async () => {
    let startDate = report.startDate;
    let endDate = report.endDate;

    if (report.preset !== "choose time preset") {
      const presetDates = getTimeFromAggregationPresets(
        report.preset,
        report.timezone
      );
      startDate = presetDates.startDate;
      endDate = presetDates.endDate;
    }

    if (!startDate || !endDate) {
      dispatch(
        setAlert({
          msg: "Please select Start Date and End Date",
          type: AlertTypes.WARNING,
          d: dispatch,
        })
      );
      return;
    }

    const formattedStartDate = dayjs(startDate).format("YYYY-MM-DD");
    const formattedEndDate = dayjs(endDate).format("YYYY-MM-DD");

    setIsExportingLeadIds(true);
    try {
      const params = new URLSearchParams();
      params.append("startDate", formattedStartDate);
      params.append("endDate", formattedEndDate);
      if (report.idCampaign && report.idCampaign !== "all") {
        params.append("id_campaign", report.idCampaign);
      }

      const response = await apiClient.get<{
        leadIds: string[];
        totalCount: number;
      }>(`${reportingBaseUrl}/lead-ids?${params.toString()}`);

      if (!response?.data?.leadIds || response.data.leadIds.length === 0) {
        dispatch(
          setAlert({
            msg: "No lead IDs found for the selected date range",
            type: AlertTypes.WARNING,
            d: dispatch,
          })
        );
        return;
      }

      const leadIds = response.data.leadIds;

      const escapeCsvValue = (value: string): string => {
        if (
          value.includes(",") ||
          value.includes('"') ||
          value.includes("\n") ||
          value.includes("\r")
        ) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      };
      const csvContent = ["lead_id", ...leadIds.map(escapeCsvValue)].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_ids_${formattedStartDate}_to_${formattedEndDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      dispatch(
        setAlert({
          msg: `Exported ${leadIds.length} lead IDs`,
          type: AlertTypes.SUCCESS,
          d: dispatch,
        })
      );
    } catch (err) {
      console.error("Failed to export lead IDs:", err);
      dispatch(
        setAlert({
          msg: "Could not export lead IDs",
          type: AlertTypes.FAILURE,
          d: dispatch,
        })
      );
    } finally {
      setIsExportingLeadIds(false);
    }
  }, [
    report.startDate,
    report.endDate,
    report.preset,
    report.timezone,
    report.idCampaign,
    dispatch,
  ]);

  const exportAllAsCsv = useCallback(async () => {
    const exportReport = formatReportDates(report);
    if (!validateFields(exportReport, false)) {
      return;
    }

    setIsExporting(true);
    try {
      const chunkSize = 10000;
      let allData: IReportingResult[] = [];
      let nextCursor: string | null = null;
      let hasMore = true;
      let totalCount = 0;

      const firstPageParams = buildQueryParams(
        exportReport,
        { page: 0, pageSize: chunkSize, cursor: null },
        false,
        false
      );

      firstPageParams.set("includeTotals", "true");
      const firstResponse = await apiClient.get<{
        data: IReportingResult[];
        totalCount: number;
        nextCursor?: string | null;
        hasMore?: boolean;
        totals?: IReportingResult | null;
      }>(`${reportingBaseUrl}?${firstPageParams.toString()}`);

      if (!firstResponse?.data?.data || firstResponse.data.data.length === 0) {
        dispatch(
          setAlert({
            msg: "No data to export",
            type: AlertTypes.WARNING,
            d: dispatch,
          })
        );
        return;
      }

      const exportTotals = firstResponse.data.totals ?? null;
      totalCount = firstResponse.data.totalCount;
      allData.push(...firstResponse.data.data);
      nextCursor = firstResponse.data.nextCursor || null;
      hasMore = firstResponse.data.hasMore ?? nextCursor !== null;

      while (hasMore && nextCursor) {
        const params = buildQueryParams(
          exportReport,
          { page: 0, pageSize: chunkSize, cursor: nextCursor },
          true,
          false
        );
        const response = await apiClient.get<{
          data: IReportingResult[];
          totalCount: number;
          nextCursor?: string | null;
          hasMore?: boolean;
        }>(`${reportingBaseUrl}?${params.toString()}`);

        if (response?.data?.data) {
          allData.push(...response.data.data);
        }

        nextCursor = response?.data?.nextCursor || null;
        hasMore = response?.data?.hasMore ?? nextCursor !== null;

        console.log(
          `Fetching export data: ${allData.length} / ${totalCount} rows`
        );
      }

      const formattedData = allData.map((row) =>
        formatReportRow(row, timestampKeys)
      );

      const exportColumns = getReportColumns(formattedData[0]);
      const formatCsvRow = (row: IReportingResult) =>
        exportColumns
          .map((col) => {
            const raw = row[col.name];
            const value = col.format
              ? formatNumericDisplay(raw, col.format)
              : raw;
            if (
              typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
            ) {
              return `"${value.replaceAll('"', '""')}"`;
            }
            return value ?? "";
          })
          .join(",");
      const csvRows = [
        exportColumns.map((col) => col.label).join(","),
        ...formattedData.map(formatCsvRow),
      ];

      if (exportTotals) {
        const labelCol = exportColumns.find(
          (col) => exportTotals[col.name] === undefined
        );
        csvRows.push(
          formatCsvRow(
            labelCol
              ? { ...exportTotals, [labelCol.name]: "OVERALL" }
              : exportTotals
          )
        );
      }
      const csvContent = csvRows.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `conversion_report_${dayjs().format(
        "YYYY-MM-DD_HH-mm-ss"
      )}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      dispatch(
        setAlert({
          msg: `Exported ${formattedData.length} rows`,
          type: AlertTypes.SUCCESS,
          d: dispatch,
        })
      );
    } catch (err) {
      console.error("Failed to export report:", err);
      dispatch(
        setAlert({
          msg: "Could not export report data",
          type: AlertTypes.FAILURE,
          d: dispatch,
        })
      );
    } finally {
      setIsExporting(false);
    }
  }, [
    report,
    dispatch,
    campaignCalculatedMetrics,
    timestampKeys,
    metadata.loaded,
    campaignConfigKey,
    expectedCampaignConfigKey,
  ]);

  const resetToFirstPage = useCallback(() => {
    cursorCacheRef.current = new Map([[0, null]]);
    paginationRef.current = {
      page: 0,
      pageSize: paginationRef.current.pageSize,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    }));
  }, []);

  const onPaginationModelChange = useCallback(
    (model: IPaginationModel) => {
      // The grid fires this when Run Report resets it to page 0, not only when the user
      // pages. Refetching on a page that did not actually change duplicates the run the
      // caller is already making.
      const pageUnchanged =
        model.page === paginationRef.current.page &&
        model.pageSize === paginationRef.current.pageSize;

      const cachedCursor = cursorCacheRef.current.get(model.page);

      const newModel: IPaginationModel = {
        ...model,
        cursor: cachedCursor ?? null,
        nextCursor: paginationRef.current.nextCursor,
        hasMore: paginationRef.current.hasMore,
      };

      setPaginationModel(newModel);
      paginationRef.current = newModel;

      if (pageUnchanged) return;
      updateReportRef();
    },
    [updateReportRef]
  );

  const onSortModelChange = useCallback(
    (model: GridSortModel) => {
      // Same as pagination: Run Report clears the sort, which makes the grid fire this
      // with a model identical to the one already held.
      const sortUnchanged = isSameSortModel(model, sortModelRef.current);

      setSortModel(model);
      sortModelRef.current = model;

      if (sortUnchanged) return;
      resetToFirstPage();
      updateReportRef();
    },
    [updateReportRef, resetToFirstPage]
  );

  const onFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      // Run Report clears the filter model, and the grid echoes that back here. Without
      // this guard the echo schedules a debounced refetch that lands 400ms after the run
      // the caller already started.
      const filterUnchanged =
        JSON.stringify(model) === JSON.stringify(gridFilterModelRef.current);

      setGridFilterModel(model);
      gridFilterModelRef.current = model;
      if (filterUnchanged) return;
      if (gridChangeTimerRef.current) clearTimeout(gridChangeTimerRef.current);
      gridChangeTimerRef.current = setTimeout(() => {
        resetToFirstPage();

        updateReportTotals(null);
        updateReportRef();
      }, 400);
    },
    [updateReportRef, resetToFirstPage]
  );

  const runReportFromBuilder = useCallback(() => {
    if (!validateFields(formatReportDates(report), false)) return;
    const resolved = formatReportDates(report);
    runDatesRef.current = {
      startDate: resolved.startDate,
      endDate: resolved.endDate,
    };
    setReport(resolved);
    resetGridSortAndFilter();
    cursorCacheRef.current = new Map([[0, null]]);
    paginationRef.current = {
      page: 0,
      pageSize: paginationRef.current.pageSize,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    };
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
      cursor: null,
      nextCursor: null,
      hasMore: false,
    }));

    updateReportTotals(null);
    // A chart read from the previous Run is answering a question nobody is asking any more, and it
    // holds a pool connection for the whole of this one. Kill it now, not when its answer arrives.
    chartCancellationRef.current?.cancel("Superseded by a new run");
    chartCancellationRef.current = null;
    chartRequestId.current += 1;
    setChartRows(null);
    setChartLoading(false);
    // Sequenced, not fired alongside: the grid's answer says whether the chart still needs its own
    // scan, and two raw-pixel scans at once hold two pool connections for one Run.
    chartDispatchPendingRef.current = true;
    void updateReportRef().then(() => {
      // Latched, because anything that resolves this a second time — a deduped refetch, a grid
      // event after the rows land — would start a second chart read whose only effect is to
      // cancel the first one mid-scan and leave the chart empty.
      if (!chartDispatchPendingRef.current) return;
      chartDispatchPendingRef.current = false;
      if (!chartServedInlineRef.current) void runChartQuery();
    });
  }, [
    report,
    metadata.loaded,
    campaignConfigKey,
    expectedCampaignConfigKey,
    updateReportRef,
    updateReportTotals,
    runChartQuery,
  ]);

  const reportContextValue = useMemo(
    () => ({
      report,
      reportTemplates,
      setReport,
      showReportSettings,
      tabIndex,
      setTabIndex,
      columns,
      reportData: reportData || null,
      chartRows,
      chartLoading,
      runReport: runReportFromBuilder,
      cancelReport,
      isFetchingReportData,
      reportError,
      metadata,
      validationErrors,
      totalCount,
      totals: reportTotals,
      paginationModel,
      onPaginationModelChange,
      sortModel,
      onSortModelChange,
      filterModel: gridFilterModel,
      onFilterModelChange,
      exportAllAsCsv,
      exportLeadIds,
      isExporting,
      isExportingLeadIds,
      campaignOptions: accessibleCampaigns,
      campaignEventOptions,
      campaignCalculatedMetrics,
    }),
    [
      report,
      showReportSettings,
      reportTemplates,
      tabIndex,
      columns,
      reportData,
      chartRows,
      chartLoading,
      isFetchingReportData,
      reportError,
      metadata,
      validationErrors,
      runReportFromBuilder,
      cancelReport,
      totalCount,
      reportTotals,
      paginationModel,
      onPaginationModelChange,
      sortModel,
      onSortModelChange,
      gridFilterModel,
      onFilterModelChange,
      exportAllAsCsv,
      exportLeadIds,
      isExporting,
      isExportingLeadIds,
      accessibleCampaigns,
      campaignEventOptions,
      campaignCalculatedMetrics,
    ]
  );

  return (
    <>
      <ReportContext.Provider value={reportContextValue}>
        <ReportTabs
          key={String(selectedOrganizationId)}
          saveReportTemplate={saveReportTemplate}
          templatesLoading={templatesLoading}
          templatesError={templatesError}
          templateBusy={templateBusy}
          refreshTemplates={refreshTemplates}
          deleteReport={deleteReport}
          runReportTemplate={runReportTemplate}
          clearReport={clearReport}
          updateReport={updateReport}
          overwriteTemplate={overwriteTemplate}
          copyTemplate={copyTemplate}
        />
      </ReportContext.Provider>
    </>
  );
};

export default ReportingModule;
